// Cars — the lot.
//
// Two different trades share this screen, and the money reads differently on
// each, so nothing here ever shows one number where two are meant:
//
//   OUR CAR         CAP bought it. Sell it and CAP keeps
//                   (sale − what it cost − work done on it).
//   SOMEONE ELSE'S  CAP holds it for the owner. Sell it and CAP keeps the
//                   commission; the rest is the owner's money, passing through.
//
// So "sold" is shown twice on purpose: what the BUYER PAID (gross — big, and
// mostly not ours on a commission car) and what CAP KEEPS (earning — the real
// figure, and the one the business totals use).
//
// The float sits at the top for the same reason: money CAP hands the car
// business is real money out of the till that day, and it stays visible until
// it comes back.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiAlertCircle,
  FiArrowDownLeft,
  FiArrowUpRight,
  FiDollarSign,
  FiKey,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiTrash2,
  FiTrendingUp,
  FiUser,
} from 'react-icons/fi';
import MainLayout from '../components/layout/MainLayout.jsx';
import { Alert, Button, PageHeader } from '../components/common/index.js';
import {
  Field,
  Modal,
  NumberInput,
  Select,
  StatCard,
  errText,
  fmtMoney,
  fmtShort,
  inputCls,
  searchIconCls,
  searchInputCls,
} from '../components/cars/controls.jsx';
import AddCarModal from '../components/cars/AddCarModal.jsx';
import { carsApi, carsFundApi } from '../api/carsApi';

const TABS = [
  { value: 'available', label: 'Available', hint: 'On the lot, ready to sell' },
  { value: 'booked', label: 'Booked', hint: 'A token has been taken' },
  { value: 'sold', label: 'Sold', hint: 'Gone — and what CAP made on it' },
  { value: '', label: 'All', hint: 'Every car ever on the lot' },
];

const OWNERSHIP = [
  { value: '', label: 'Ours and theirs' },
  { value: 'own', label: 'Our cars only' },
  { value: 'consignment', label: "Other owners' only" },
];

const STATUS_TONE = {
  available: 'bg-emerald-50 text-emerald-700',
  booked: 'bg-amber-50 text-amber-700',
  sold: 'bg-slate-100 text-slate-600',
  returned: 'bg-rose-50 text-rose-700',
};

const today = () => new Date().toISOString().slice(0, 10);

// ── The CAP ↔ Cars float ─────────────────────────────────────────────────────
const FundModal = ({ open, onClose, cars, onSaved }) => {
  const [direction, setDirection] = useState('to_cars');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(today());
  const [note, setNote] = useState('');
  const [carId, setCarId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) { setDirection('to_cars'); setAmount(''); setDate(today()); setNote(''); setCarId(''); setError(''); }
  }, [open]);

  const submit = async () => {
    setError('');
    if (!amount || Number(amount) <= 0) { setError('Enter an amount.'); return; }
    setBusy(true);
    try {
      await carsFundApi.create({
        direction,
        amount,
        transferred_on: date,
        note,
        ...(carId ? { car: carId } : {}),
      });
      onSaved();
      onClose();
    } catch (e) {
      setError(errText(e, 'Could not record the transfer.'));
    } finally {
      setBusy(false);
    }
  };

  const giving = direction === 'to_cars';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Money between CAP and Cars"
      subtitle="Money out of CAP shows in that day's expenses. Money back is added to the balance the day it lands."
    >
      {error && <Alert type="error" message={error} className="mb-4" />}
      <div className="space-y-3">
        <Field label="Which way?" required>
          <Select
            value={direction}
            onChange={setDirection}
            options={[
              { value: 'to_cars', label: 'CAP gives money to Cars', hint: "Leaves the till — lands in today's expenses" },
              { value: 'to_cap', label: 'Cars gives it back to CAP', hint: 'Comes back in — added to the balance' },
            ]}
          />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Amount" required>
            <NumberInput value={amount} onChange={setAmount} placeholder="500000" />
          </Field>
          <Field label="On" required>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
          </Field>
        </div>
        {giving && (
          <Field label="For which car?" hint="Optional — leave blank if it is general working money.">
            <Select
              value={carId}
              onChange={setCarId}
              placeholder="Not for one car in particular"
              options={[
                { value: '', label: 'Not for one car in particular' },
                ...cars.map((c) => ({ value: String(c.id), label: c.display_name })),
              ]}
            />
          </Field>
        )}
        <Field label="Note">
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="working money for the month" className={inputCls} />
        </Field>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy}>Record it</Button>
      </div>
    </Modal>
  );
};

const FundLedger = ({ transfers, onDelete, busyId }) => {
  if (!transfers.length) {
    return <p className="px-4 py-6 text-center text-sm text-slate-400">No money has moved between CAP and Cars yet.</p>;
  }
  return (
    <ul className="divide-y divide-slate-100">
      {transfers.map((t) => {
        const giving = t.direction === 'to_cars';
        return (
          <li key={t.id} className="flex items-center gap-3 px-4 py-3">
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
              giving ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'
            }`}>
              {giving ? <FiArrowUpRight className="h-4 w-4" /> : <FiArrowDownLeft className="h-4 w-4" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-slate-800">
                {giving ? 'Given to Cars' : 'Back from Cars'} · {fmtMoney(t.amount)}
              </p>
              <p className="truncate text-xs text-slate-400">
                {t.transferred_on_display}
                {t.car_display ? ` · ${t.car_display}` : ''}
                {t.note ? ` · ${t.note}` : ''}
              </p>
            </div>
            <button
              type="button"
              disabled={busyId === t.id}
              onClick={() => onDelete(t)}
              className="rounded-lg p-2 text-slate-300 transition hover:bg-rose-50 hover:text-rose-600 disabled:opacity-40"
              aria-label="Remove"
            >
              <FiTrash2 className="h-4 w-4" />
            </button>
          </li>
        );
      })}
    </ul>
  );
};

// ── The page ─────────────────────────────────────────────────────────────────
const CarsBoard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('available');
  const [ownership, setOwnership] = useState('');
  const [query, setQuery] = useState('');
  const [cars, setCars] = useState([]);
  const [summary, setSummary] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [fund, setFund] = useState({ transfers: [], outstanding: '0.00', given: '0.00', returned: '0.00' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [fundOpen, setFundOpen] = useState(false);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [deletingFund, setDeletingFund] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, transfers] = await Promise.all([
        carsApi.getList({
          status: tab || undefined,
          ownership: ownership || undefined,
          q: query.trim() || undefined,
        }),
        carsFundApi.getList(),
      ]);
      setCars(list.data.cars || []);
      setSummary(list.data.summary || null);
      setFund(transfers.data || {});
    } catch (e) {
      setError(errText(e, 'Could not load the cars.'));
    } finally {
      setLoading(false);
    }
  }, [tab, ownership, query]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    carsApi.getCatalog()
      .then(({ data }) => setCatalog(Array.isArray(data) ? data : data?.brands || []))
      .catch(() => setCatalog([]));   // the picker still takes typed cars
  }, []);

  const removeTransfer = async (t) => {
    setDeletingFund(t.id);
    try {
      await carsFundApi.remove(t.id);
      await load();
    } catch (e) {
      setError(errText(e, 'Could not remove the transfer.'));
    } finally {
      setDeletingFund(null);
    }
  };

  const stock = summary?.stock || {};
  const outstanding = Number(fund.outstanding || 0);

  const tabHint = useMemo(() => TABS.find((t) => t.value === tab)?.hint || '', [tab]);

  return (
    <>
      <PageHeader
        icon={FiKey}
        title="Cars"
        meta={summary ? `${stock.total || 0} car(s) on the books · ${stock.own || 0} ours, ${stock.consignment || 0} on commission` : null}
      >
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-60"
        >
          <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
        <Button variant="secondary" onClick={() => setFundOpen(true)}>
          <FiDollarSign className="mr-1.5 h-4 w-4" /> Money to / from Cars
        </Button>
        <Button onClick={() => setAddOpen(true)}>
          <FiPlus className="mr-1.5 h-4 w-4" /> Add car
        </Button>
      </PageHeader>

      {error && <Alert type="error" message={error} className="mb-4" />}

      {/* The five numbers that say where this business stands */}
      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard
          icon={FiKey}
          tone="violet"
          label="On the lot"
          value={`${(stock.available || 0) + (stock.booked || 0)} cars`}
          hint={`${stock.booked || 0} booked · asking ${fmtShort(stock.asking_value)}`}
        />
        <StatCard
          icon={FiDollarSign}
          tone="amber"
          label="Money in the lot"
          value={fmtShort(stock.invested)}
          hint="our cars + work done on them"
        />
        <StatCard
          icon={FiTrendingUp}
          tone="sky"
          label="Buyers paid"
          value={fmtShort(summary?.gross_value)}
          hint={`${summary?.sold_count || 0} sold — owners' share included`}
        />
        <StatCard
          icon={FiTrendingUp}
          tone="emerald"
          label="CAP earned"
          value={fmtShort(summary?.earning_value)}
          hint="what is actually ours"
        />
        <button type="button" onClick={() => setLedgerOpen(true)} className="text-left">
          <StatCard
            icon={outstanding > 0 ? FiArrowUpRight : FiArrowDownLeft}
            tone={outstanding > 0 ? 'rose' : 'slate'}
            label="Cars holds of CAP's"
            value={fmtShort(fund.outstanding)}
            hint={`given ${fmtShort(fund.given)} · back ${fmtShort(fund.returned)}`}
          />
        </button>
      </div>

      {/* Anything owed — said plainly rather than left in a column */}
      {(Number(summary?.owner_due_value || 0) > 0 || Number(summary?.balance_due_value || 0) > 0) && (
        <div className="mb-5 flex flex-wrap gap-2">
          {Number(summary?.owner_due_value || 0) > 0 && (
            <span className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700">
              <FiAlertCircle className="h-3.5 w-3.5" />
              {fmtMoney(summary.owner_due_value)} of owners' money is still with us
            </span>
          )}
          {Number(summary?.balance_due_value || 0) > 0 && (
            <span className="inline-flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-700">
              <FiAlertCircle className="h-3.5 w-3.5" />
              {fmtMoney(summary.balance_due_value)} still to collect from buyers
            </span>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-white p-1">
          {TABS.map((t) => (
            <button
              key={t.value || 'all'}
              type="button"
              onClick={() => setTab(t.value)}
              className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                tab === t.value ? 'bg-violet-600 text-white' : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="w-44">
          <Select value={ownership} onChange={setOwnership} options={OWNERSHIP} />
        </div>
        <div className="relative min-w-[200px] flex-1">
          <FiSearch className={searchIconCls} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search car, registration, owner…"
            className={searchInputCls}
          />
        </div>
      </div>
      {tabHint && <p className="mb-3 text-xs text-slate-400">{tabHint}</p>}

      {/* The lot */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {loading && (
          <div className="space-y-2 p-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-slate-100" />)}
          </div>
        )}

        {!loading && cars.length === 0 && (
          <div className="px-6 py-14 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100">
              <FiKey className="h-6 w-6 text-slate-400" />
            </div>
            <p className="text-sm font-bold text-slate-700">No cars here</p>
            <p className="mx-auto mt-1 max-w-sm text-xs text-slate-400">
              {tab === 'available'
                ? 'Nothing is on the lot right now. Add a car and it shows up here.'
                : 'Nothing matches this filter.'}
            </p>
          </div>
        )}

        {!loading && cars.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60 text-left text-[11px] font-bold uppercase tracking-wide text-slate-400">
                  <th className="px-4 py-3">Car</th>
                  <th className="px-4 py-3">Whose</th>
                  <th className="px-4 py-3 text-right">Cost</th>
                  <th className="px-4 py-3 text-right">Asking</th>
                  <th className="px-4 py-3 text-right">Sold for</th>
                  <th className="px-4 py-3 text-right">CAP earns</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {cars.map((car) => {
                  const pnl = car.pnl || {};
                  const sold = Boolean(car.sale);
                  const earning = sold ? pnl.cap_earning : pnl.expected_earning;
                  return (
                    <tr
                      key={car.id}
                      onClick={() => navigate(`/cars/${car.id}`)}
                      className="cursor-pointer transition hover:bg-violet-50/40"
                    >
                      <td className="px-4 py-3">
                        <p className="font-bold text-slate-900">{car.display_name}</p>
                        <p className="text-xs text-slate-400">
                          {[car.registration_number, car.variant_label, car.km_driven ? `${Number(car.km_driven).toLocaleString('en-IN')} km` : '']
                            .filter(Boolean).join(' · ')}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        {car.ownership === 'consignment' ? (
                          <>
                            <p className="flex items-center gap-1.5 text-xs font-bold text-slate-700">
                              <FiUser className="h-3 w-3 text-slate-400" /> {car.owner_name || 'Owner'}
                            </p>
                            <p className="text-[11px] font-semibold text-violet-600">{car.commission_label}</p>
                          </>
                        ) : (
                          <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600">Ours</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-600">{fmtMoney(pnl.cost)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-600">{fmtMoney(car.asking_price)}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-800">
                        {sold ? fmtMoney(car.sale.sold_price) : '—'}
                      </td>
                      <td className={`px-4 py-3 text-right tabular-nums font-bold ${
                        sold ? 'text-emerald-600' : 'text-slate-400'
                      }`}>
                        {earning == null ? '—' : fmtMoney(earning)}
                        {!sold && earning != null && (
                          <span className="block text-[10px] font-semibold uppercase text-slate-300">if it goes at asking</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`rounded-md px-2 py-1 text-[11px] font-bold ${STATUS_TONE[car.status] || 'bg-slate-100 text-slate-600'}`}>
                          {car.status_label}
                        </span>
                        {sold && car.sale.balance_due !== '0.00' && (
                          <span className="mt-1 block text-[10px] font-bold text-amber-600">
                            {fmtMoney(car.sale.balance_due)} owed
                          </span>
                        )}
                        {sold && car.ownership === 'consignment' && !car.sale.owner_paid && (
                          <span className="mt-1 block text-[10px] font-bold text-rose-600">owner unpaid</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AddCarModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        catalog={catalog}
        onSaved={load}
        createFn={carsApi.create}
      />
      <FundModal open={fundOpen} onClose={() => setFundOpen(false)} cars={cars} onSaved={load} />

      <Modal
        open={ledgerOpen}
        onClose={() => setLedgerOpen(false)}
        title="Money between CAP and Cars"
        subtitle={`Cars is holding ${fmtMoney(fund.outstanding)} of CAP's money.`}
      >
        <div className="-mx-5 -mb-4 max-h-[60vh] overflow-y-auto border-t border-slate-100">
          <FundLedger transfers={fund.transfers || []} onDelete={removeTransfer} busyId={deletingFund} />
        </div>
      </Modal>
    </>
  );
};

const CarsPage = () => (
  <MainLayout>
    <CarsBoard />
  </MainLayout>
);

export default CarsPage;
